import { types } from 'mobx-state-tree';
import { NewOrExisting, NewOrExistingFacility } from '../../models';

export const OrganizationModel = types.model({
  id: types.number,
  name: types.string,
  facilityIds: types.array(types.number),
  createdAt: types.string,
  updatedAt: types.string,
});

const NewOrExistingEnum = types.enumeration<string>([NewOrExisting.NEW, NewOrExisting.EXISTING]);

const NewOrExistingFacilityEnum = types.enumeration<string>([
  NewOrExistingFacility.NEW,
  NewOrExistingFacility.EXISTING,
]);

const NewDigitalOrderOrganization = types.model({
  organizationId: types.maybe(types.number),
  name: types.string,
  isActive: types.union(types.number, types.string),
  newOrExistingOrganization: NewOrExistingEnum,
});

const ExistingDigitalOrderOrganization = types.model({
  organizationId: types.number,
  newOrExistingOrganization: NewOrExistingEnum,
  keyAccount: types.maybe(types.boolean),
});

export const OrderOrganizationModel = types.union(
  {
    dispatcher: snapshot => {
      return snapshot && snapshot.newOrExistingOrganization === NewOrExisting.NEW
        ? NewDigitalOrderOrganization
        : ExistingDigitalOrderOrganization;
    },
  },
  ExistingDigitalOrderOrganization,
  NewDigitalOrderOrganization,
);

const OrderFacilityModel = types.model({
  type: types.maybe(NewOrExistingFacilityEnum),
  facilityId: types.maybe(types.number),
  city: types.maybeNull(types.string),
  countryId: types.maybeNull(types.number),
  stateId: types.maybeNull(types.number),
  isActive: types.maybeNull(types.string), // '1' or '0'
  name: types.maybeNull(types.string),
  nickname: types.maybeNull(types.string),
  postalCode: types.maybeNull(types.string),
  street1: types.maybeNull(types.string),
  street2: types.maybeNull(types.string),
});

const FacilityInformation = types.model({
  facilities: types.array(OrderFacilityModel),
  organizationId: types.maybe(types.number),
  isActive: types.maybe(types.string),
});

const ProductInformation = types.model({
  integrations: types.maybe(types.frozen()),
  productSelection: types.maybe(types.frozen()),
});

export const DigitalOrdersBaseModel = types.model({
  id: types.string,
  userId: types.maybe(types.string),
  created: types.number,
  updated: types.number,
  organization: types.maybe(OrderOrganizationModel),
  facility: types.maybe(FacilityInformation),
  products: types.maybe(ProductInformation),
  productDetails: types.maybe(types.frozen()),
  submitted: types.maybe(types.boolean),
  orderStatus: types.maybe(types.string),
  fulfillmentUser: types.maybe(types.string),
  fulfillmentTime: types.maybe(types.number),
  orderActionsComplete: types.maybe(types.number),
});

export const FacilityModel = types.model({
  id: types.number,
  name: types.string,
  organizationId: types.number,
  createdAt: types.string,
  updatedAt: types.string,
  timezone: types.maybeNull(types.string),
  address: types.maybeNull(
    types.model({
      city: types.string,
      createdAt: types.string,
      id: types.number,
      street1: types.string,
      street2: types.string,
      updatedAt: types.string,
    }),
  ),
  country: types.maybeNull(
    types.model({
      id: types.number,
      iso: types.string,
      iso3: types.string,
      name: types.string,
      printableName: types.string,
    }),
  ),
  state: types.maybeNull(
    types.model({
      id: types.number,
      iso: types.string,
      name: types.string,
      printableName: types.string,
    }),
  ),
});

export const CountryModel = types.model({
  id: types.number,
  iso: types.string,
  iso3: types.maybeNull(types.string),
  name: types.string,
  printableName: types.string,
});
export const StateModel = types.model({
  id: types.number,
  iso: types.string,
  countryId: types.maybeNull(types.number),
  name: types.string,
  printableName: types.string,
});
export const AddressModel = types.model({
  city: types.string,
  street1: types.string,
  street2: types.maybeNull(types.string),
  postalCode: types.maybeNull(types.string),
  stateId: types.maybeNull(types.number),
  countryId: types.maybeNull(types.number),
  createdAt: types.string,
  updatedAt: types.string,
});

export const FullFacilityModel = types.model({
  id: types.number,
  name: types.string,
  organizationId: types.number,
  createdAt: types.string,
  updatedAt: types.string,
  timezone: types.maybeNull(types.string),
  address: types.maybeNull(AddressModel),
  country: types.maybeNull(CountryModel),
  state: types.maybeNull(StateModel),
});

export const OrderProductsModel = types.model({
  product_selection: types.frozen(),
  integrations: types.frozen(),
});

export const OrderByStatus = types.union(
  DigitalOrdersBaseModel,
  types.model({
    name: types.maybe(types.string),
    email: types.maybe(types.string),
    summary: types.maybe(types.string),
  }),
);
